import { AxiosResponse, AxiosError } from 'axios';
import api from './api.service';

// Import the Product type
import { Product } from '../types/product.types';

export interface InventoryTransaction {
    id: number;
    productId: number;
    productName: string;
    productSku: string;
    quantityChange: number;
    previousQuantity: number;
    newQuantity: number;
    transactionType: string;
    referenceId?: number;
    referenceType?: string;
    userId?: number;
    userName?: string;
    notes?: string;
    createdAt: string;
}

export interface InventoryTransactionResponse {
    content: InventoryTransaction[];
    totalElements: number;
    totalPages: number;
    last: boolean;
    size: number;
    number: number;
    first: boolean;
    empty: boolean;
}

class InventoryService {
    private readonly baseUrl = '/inventory';

    // Helper method to handle errors
    private handleError(error: any, operation: string): never {
        let errorMessage = `Error during ${operation}`;
        
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            errorMessage = `Server error (${error.response.status}): ${error.response.data?.message || 'Unknown error'}`;
        } else if (error.request) {
            // The request was made but no response was received
            errorMessage = 'No response received from server. Please check your network connection.';
        }
        
        console.error(`Inventory Service: ${errorMessage}`, error);
        throw new Error(errorMessage);
    }

    // Update product stock quantity
    async updateProductStock(productId: number, quantity: number, operation: 'SET' | 'ADD' | 'SUBTRACT' = 'SET'): Promise<Product> {
        try {
            console.log(`Updating product ${productId} stock with quantity ${quantity} using operation ${operation}`);
            const response = await api.patch(
                `${this.baseUrl}/products/${productId}/stock?quantity=${quantity}&operation=${operation}`
            ) as AxiosResponse<Product>;
            return response.data;
        } catch (error) {
            return this.handleError(error, `updating stock for product ${productId}`);
        }
    }

    // Get low stock products
    async getLowStockProducts(): Promise<Product[]> {
        try {
            const response = await api.get(`${this.baseUrl}/low-stock`) as AxiosResponse<Product[]>;
            return response.data;
        } catch (error) {
            return this.handleError(error, 'fetching low stock products');
        }
    }

    // Toggle inventory tracking for a product
    async toggleInventoryTracking(productId: number, track: boolean): Promise<Product> {
        try {
            const response = await api.patch(
                `${this.baseUrl}/products/${productId}/track-inventory?track=${track}`
            ) as AxiosResponse<Product>;
            return response.data;
        } catch (error) {
            return this.handleError(error, `toggling inventory tracking for product ${productId}`);
        }
    }

    // Update minimum stock level for a product
    async updateMinimumStockLevel(productId: number, level: number): Promise<Product> {
        try {
            const response = await api.patch(
                `${this.baseUrl}/products/${productId}/minimum-stock?level=${level}`
            ) as AxiosResponse<Product>;
            return response.data;
        } catch (error) {
            return this.handleError(error, `updating minimum stock level for product ${productId}`);
        }
    }

    // Get all inventory transactions
    async getAllTransactions(page: number = 0, size: number = 20): Promise<InventoryTransactionResponse> {
        try {
            const response = await api.get(
                `${this.baseUrl}/transactions?page=${page}&size=${size}`
            ) as AxiosResponse<InventoryTransactionResponse>;
            return response.data;
        } catch (error) {
            // If we fail to get transactions, return an empty result
            console.error('Error fetching inventory transactions:', error);
            return {
                content: [],
                totalElements: 0,
                totalPages: 0,
                last: true,
                size: size,
                number: page,
                first: true,
                empty: true
            };
        }
    }

    // Get transactions for a specific product
    async getTransactionsByProduct(productId: number, page: number = 0, size: number = 20): Promise<InventoryTransactionResponse> {
        try {
            const response = await api.get(
                `${this.baseUrl}/transactions/product/${productId}?page=${page}&size=${size}`
            ) as AxiosResponse<InventoryTransactionResponse>;
            return response.data;
        } catch (error) {
            // If we fail to get transactions, return an empty result
            console.error(`Error fetching transactions for product ${productId}:`, error);
            return {
                content: [],
                totalElements: 0,
                totalPages: 0,
                last: true,
                size: size,
                number: page,
                first: true,
                empty: true
            };
        }
    }

    // Get transactions by date range
    async getTransactionsByDateRange(startDate: string, endDate: string): Promise<InventoryTransaction[]> {
        try {
            const response = await api.get(
                `${this.baseUrl}/transactions/date-range?startDate=${startDate}&endDate=${endDate}`
            ) as AxiosResponse<InventoryTransaction[]>;
            return response.data;
        } catch (error) {
            console.error('Error fetching transactions by date range:', error);
            return [];
        }
    }

    // Get transactions by type
    async getTransactionsByType(transactionType: string): Promise<InventoryTransaction[]> {
        try {
            const response = await api.get(
                `${this.baseUrl}/transactions/type/${transactionType}`
            ) as AxiosResponse<InventoryTransaction[]>;
            return response.data;
        } catch (error) {
            console.error(`Error fetching transactions of type ${transactionType}:`, error);
            return [];
        }
    }
}

const inventoryService = new InventoryService();
export default inventoryService; 